import { Injectable } from '@angular/core';
import { BackendService } from './general/backend.service';
import { ToastService } from './general/toast.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class CkmService {

  constructor(
    private backend: BackendService,
    private toast: ToastService,
    private translateService: TranslateService
  ) {}

  // --- CKM Simulations ---

  /**
   * Recupera la lista delle simulazioni CKM per un determinato progetto.
   * @param projectId ID del progetto.
   */
  public getCkmSimulationsList(
    projectId: string,
    successCallback: (data: any) => void = (data: any) => {},
    errorCallback: (error: any) => void = (error: any) => {}
  ): void {
    this.backend.get(`ckm-simulations/list/${projectId}`).subscribe(
      result => successCallback(result),
      error => {
        console.error(error);
        this.backend.showErrors(error);
        errorCallback(error);
      }
    );
  }

  /**
   * Recupera i dettagli di una simulazione CKM.
   * @param id ID della simulazione.
   */
  public getCkmSimulationDetails(
    id: string,
    successCallback: (data: any) => void = (data: any) => {},
    errorCallback: (error: any) => void = (error: any) => {}
  ): void {
    this.backend.get(`ckm-simulations/details/${id}`).subscribe(
      result => successCallback(result),
      error => {
        console.error(error);
        this.backend.showErrors(error);
        errorCallback(error);
      }
    );
  }

  /**
   * Crea una nuova simulazione CKM per un determinato progetto.
   * @param projectId ID progetto.
   * @param model Dati della simulazione da creare.
   */
  public createCkmSimulation(
    projectId: string,
    model: any,
    successCallback: (data: any) => void = (data: any) => {},
    errorCallback: (error: any) => void = (error: any) => {}
  ): void {
    this.backend.post(`ckm-simulations/create/${projectId}`, model).subscribe(
      result => {
        this.toast.success(
          this.translateService.instant('pages.home.component.check-decision-table'),
          this.translateService.instant('pages.ckm.simulationCreatedSuccessfully')
        );
        successCallback(result);
      },
      error => {
        console.error(error);
        this.backend.showErrors(error);
        errorCallback(error);
      }
    );
  }

  /**
   * Aggiorna una simulazione CKM esistente.
   * @param id ID della simulazione.
   * @param model Dati aggiornati della simulazione.
   */
  public updateCkmSimulation(
    id: string,
    model: any,
    successCallback: (data: any) => void = (data: any) => {},
    errorCallback: (error: any) => void = (error: any) => {}
  ): void {
    this.backend.put(`ckm-simulations/update/${id}`, model).subscribe(
      result => {
        this.toast.success(
          this.translateService.instant('generic.success'),
          this.translateService.instant('pages.ckm.simulationUpdatedSuccessfully')
        );
        successCallback(result);
      },
      error => {
        console.error(error);
        this.backend.showErrors(error);
        errorCallback(error);
      }
    );
  }

  /**
   * Elimina una simulazione CKM.
   * @param id ID della simulazione.
   */
  public deleteCkmSimulation(
    id: string,
    successCallback: (data: any) => void = (data: any) => {},
    errorCallback: (error: any) => void = (error: any) => {}
  ): void {
    this.backend.destroy(`ckm-simulations/delete/${id}`).subscribe(
      result => successCallback(result),
      error => {
        console.error(error);
        this.backend.showErrors(error);
        errorCallback(error);
      }
    );
  }

  // --- CKM Actions ---

  /**
   * Recupera la lista delle azioni CKM per una simulazione.
   * @param ckmSimulationId ID della simulazione CKM.
   */
  public getCkmActionsList(
    ckmSimulationId: string,
    successCallback: (data: any) => void = (data: any) => {},
    errorCallback: (error: any) => void = (error: any) => {}
  ): void {
    this.backend.get(`ckm-actions/list/${ckmSimulationId}`).subscribe(
      result => successCallback(result),
      error => {
        console.error(error);
        this.backend.showErrors(error);
        errorCallback(error);
      }
    );
  }

  /**
   * Recupera i dettagli di una mappa CKM.
   * @param id ID della mappa.
   */
  public getCkmMapDetails(
    id: string,
    successCallback: (data: any) => void = (data: any) => {},
    errorCallback: (error: any) => void = (error: any) => {}
  ): void {
    this.backend.get(`ckm-maps/details/${id}`).subscribe(
      result => successCallback(result),
      error => {
        console.error(error);
        this.backend.showErrors(error);
        errorCallback(error);
      }
    );
  }


  /**
   * Recupera i dettagli di un'azione CKM.
   * @param id ID dell'azione.
   */
  public getCkmActionDetails(
    id: string,
    successCallback: (data: any) => void = (data: any) => {},
    errorCallback: (error: any) => void = (error: any) => {}
  ): void {
    this.backend.get(`ckm-actions/details/${id}`).subscribe(
      result => successCallback(result),
      error => {
        console.error(error);
        this.backend.showErrors(error);
        errorCallback(error);
      }
    );
  }

  /**
   * Crea una nuova azione CKM per una simulazione.
   * @param ckmSimulationId ID della simulazione CKM.
   * @param model Dati dell'azione da creare.
   */
  public createCkmAction(
    ckmSimulationId: string,
    model: any,
    successCallback: (data: any) => void = (data: any) => {},
    errorCallback: (error: any) => void = (error: any) => {}
  ): void {
    this.backend.post(`ckm-actions/create/${ckmSimulationId}`, model).subscribe(
      result => {
        this.toast.success(
          this.translateService.instant('pages.home.component.check-decision-table'),
          this.translateService.instant('pages.ckm.actionCreatedSuccessfully')
        );
        successCallback(result);
      },
      error => {
        console.error(error);
        this.backend.showErrors(error);
        errorCallback(error);
      }
    );
  }

  /**
   * Aggiorna un'azione CKM esistente.
   * @param id ID dell'azione.
   * @param model Dati aggiornati dell'azione.
   */
  public updateCkmAction(
    id: string,
    model: any,
    successCallback: (data: any) => void = (data: any) => {},
    errorCallback: (error: any) => void = (error: any) => {}
  ): void {
    this.backend.put(`ckm-actions/update/${id}`, model).subscribe(
      result => {
        this.toast.success(
          this.translateService.instant('pages.home.component.check-decision-table'),
          this.translateService.instant('pages.ckm.actionUpdatedSuccessfully')
        );
        successCallback(result);
      },
      error => {
        console.error(error);
        this.backend.showErrors(error);
        errorCallback(error);
      }
    );
  }

  /**
   * Elimina un'azione CKM.
   * @param id ID dell'azione.
   */
  public deleteCkmAction(
    id: string,
    successCallback: (data: any) => void = (data: any) => {},
    errorCallback: (error: any) => void = (error: any) => {}
  ): void {
    this.backend.destroy(`ckm-actions/delete/${id}`).subscribe(
      result => successCallback(result),
      error => {
        console.error(error);
        this.backend.showErrors(error);
        errorCallback(error);
      }
    );
  }

  // --- CKM Events ---

  /**
   * Recupera la lista degli eventi CKM per una simulazione.
   * @param ckmSimulationId ID della simulazione CKM.
   */
  public getCkmEventsList(
    ckmSimulationId: string,
    successCallback: (data: any) => void = (data: any) => {},
    errorCallback: (error: any) => void = (error: any) => {}
  ): void {
    this.backend.get(`ckm-events/list/${ckmSimulationId}`).subscribe(
      result => successCallback(result),
      error => {
        console.error(error);
        this.backend.showErrors(error);
        errorCallback(error);
      }
    );
  }

  /**
   * Recupera i dettagli di un evento CKM.
   * @param id ID dell'evento.
   */
  public getCkmEventDetails(
    id: string,
    successCallback: (data: any) => void = (data: any) => {},
    errorCallback: (error: any) => void = (error: any) => {}
  ): void {
    this.backend.get(`ckm-events/details/${id}`).subscribe(
      result => successCallback(result),
      error => {
        console.error(error);
        this.backend.showErrors(error);
        errorCallback(error);
      }
    );
  }

  /**
   * Crea un nuovo evento CKM per una simulazione.
   * @param ckmSimulationId ID della simulazione CKM.
   * @param model Dati dell'evento da creare.
   */
  public createCkmEvent(
    ckmSimulationId: string,
    model: any,
    successCallback: (data: any) => void = (data: any) => {},
    errorCallback: (error: any) => void = (error: any) => {}
  ): void {
    this.backend.post(`ckm-events/create/${ckmSimulationId}`, model).subscribe(
      result => {
        this.toast.success(
          this.translateService.instant('pages.home.component.check-decision-table'),
          this.translateService.instant('pages.ckm.eventCreatedSuccessfully')
        );
        successCallback(result);
      },
      error => {
        console.error(error);
        this.backend.showErrors(error);
        errorCallback(error);
      }
    );
  }

  /**
   * Aggiorna un evento CKM esistente.
   * @param id ID dell'evento.
   * @param model Dati aggiornati dell'evento.
   */
  public updateCkmEvent(
    id: string,
    model: any,
    successCallback: (data: any) => void = (data: any) => {},
    errorCallback: (error: any) => void = (error: any) => {}
  ): void {
    this.backend.put(`ckm-events/update/${id}`, model).subscribe(
      result => {
        this.toast.success(
          this.translateService.instant('pages.home.component.check-decision-table'),
          this.translateService.instant('pages.ckm.eventUpdatedSuccessfully')
        );
        successCallback(result);
      },
      error => {
        console.error(error);
        this.backend.showErrors(error);
        errorCallback(error);
      }
    );
  }

  /**
   * Elimina un evento CKM.
   * @param id ID dell'evento.
   */
  public deleteCkmEvent(
    id: string,
    successCallback: (data: any) => void = (data: any) => {},
    errorCallback: (error: any) => void = (error: any) => {}
  ): void {
    this.backend.destroy(`ckm-events/delete/${id}`).subscribe(
      result => successCallback(result),
      error => {
        console.error(error);
        this.backend.showErrors(error);
        errorCallback(error);
      }
    );
  }

  // --- CKM Impact Factors ---

  /**
   * Recupera la lista dei fattori di impatto CKM per una simulazione.
   * @param ckmSimulationId ID della simulazione CKM.
   */
  public getCkmImpactFactorsList(
    ckmSimulationId: string,
    successCallback: (data: any) => void = (data: any) => {},
    errorCallback: (error: any) => void = (error: any) => {}
  ): void {
    this.backend.get(`ckm-impact-factors/list/${ckmSimulationId}`).subscribe(
      result => successCallback(result),
      error => {
        console.error(error);
        this.backend.showErrors(error);
        errorCallback(error);
      }
    );
  }

  /**
   * Recupera i dettagli di un fattore di impatto CKM.
   * @param id ID del fattore di impatto.
   */
  public getCkmImpactFactorDetails(
    id: string,
    successCallback: (data: any) => void = (data: any) => {},
    errorCallback: (error: any) => void = (error: any) => {}
  ): void {
    this.backend.get(`ckm-impact-factors/details/${id}`).subscribe(
      result => successCallback(result),
      error => {
        console.error(error);
        this.backend.showErrors(error);
        errorCallback(error);
      }
    );
  }

  public getCkmMapSurveyQuestions(
    id: string,
    successCallback: (data: any) => void = (data: any) => {},
    errorCallback: (error: any) => void = (error: any) => {}
  ): void {
    this.backend.get(`ckm-maps/details/${id}/survey-questions`).subscribe(
      result => successCallback(result),
      error => {
        console.error(error);
        this.backend.showErrors(error);
        errorCallback(error);
      }
    );
  }

  /**
   * Crea un nuovo fattore di impatto CKM per una simulazione.
   * @param ckmSimulationId ID della simulazione CKM.
   * @param model Dati del fattore di impatto da creare.
   */
  public createCkmImpactFactor(
    ckmSimulationId: string,
    model: any,
    successCallback: (data: any) => void = (data: any) => {},
    errorCallback: (error: any) => void = (error: any) => {}
  ): void {
    this.backend.post(`ckm-impact-factors/create/${ckmSimulationId}`, model).subscribe(
      result => {
        this.toast.success(
          this.translateService.instant('pages.home.component.check-decision-table'),
          this.translateService.instant('pages.ckm.impactFactorCreatedSuccessfully')
        );
        successCallback(result);
      },
      error => {
        console.error(error);
        this.backend.showErrors(error);
        errorCallback(error);
      }
    );
  }

  /**
   * Aggiorna un fattore di impatto CKM esistente.
   * @param id ID del fattore di impatto.
   * @param model Dati aggiornati del fattore di impatto.
   */
  public updateCkmImpactFactor(
    id: string,
    model: any,
    successCallback: (data: any) => void = (data: any) => {},
    errorCallback: (error: any) => void = (error: any) => {}
  ): void {
    this.backend.put(`ckm-impact-factors/update/${id}`, model).subscribe(
      result => {
        this.toast.success(
          this.translateService.instant('pages.home.component.check-decision-table'),
          this.translateService.instant('pages.ckm.impactFactorUpdatedSuccessfully')
        );
        successCallback(result);
      },
      error => {
        console.error(error);
        this.backend.showErrors(error);
        errorCallback(error);
      }
    );
  }

  /**
   * Elimina un fattore di impatto CKM.
   * @param id ID del fattore di impatto.
   */
  public deleteCkmImpactFactor(
    id: string,
    successCallback: (data: any) => void = (data: any) => {},
    errorCallback: (error: any) => void = (error: any) => {}
  ): void {
    this.backend.destroy(`ckm-impact-factors/delete/${id}`).subscribe(
      result => successCallback(result),
      error => {
        console.error(error);
        this.backend.showErrors(error);
        errorCallback(error);
      }
    );
  }
}
